import { Fragment } from 'react'
import type { JSX } from 'react'

import type { Translation } from './i18n.js'

/**
 * One whole sentence with some holes filled by elements instead of text: a date or a job number
 * that must stay left to right, or a link to another record.
 *
 * The message is still translated as one sentence (Appendix S). Element values go in as a marker
 * the translator cannot mistake for words, and the translated text is cut at the markers
 * afterwards, so a language that puts the hole first still reads in its own order.
 */
const HOLE_OPEN = '\uE000'
const HOLE_CLOSE = '\uE001'
// The backslash is doubled because this is a template literal, not a regex literal: `\w` in a
// string is just `w`, which would match only a hole literally named `w`.
const HOLE = new RegExp(`${HOLE_OPEN}(\\w+)${HOLE_CLOSE}`)

export function sentence(
  t: Translation['t'],
  key: Parameters<Translation['t']>[0],
  params: Readonly<Record<string, string | number | JSX.Element>>,
): JSX.Element {
  const text: Record<string, string | number> = {}
  for (const [name, value] of Object.entries(params)) {
    text[name] =
      typeof value === 'string' || typeof value === 'number'
        ? value
        : `${HOLE_OPEN}${name}${HOLE_CLOSE}`
  }
  const parts = t(key, text).split(HOLE)
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 0 ? (
          part === '' ? null : (
            <Fragment key={i}>{part}</Fragment>
          )
        ) : (
          <Fragment key={i}>{params[part]}</Fragment>
        ),
      )}
    </>
  )
}
